import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogHero from './components/BlogHero';
import BlogBreadcrumb from './components/BlogBreadcrumb';
import BlogMobileTOC from './components/BlogMobileTOC';
import ArticleRenderer from './components/ArticleRenderer';
import BlogFAQ from './components/BlogFAQ';
import BlogTags from './components/BlogTags';
import BlogSidebar from './components/BlogSidebar';
import { useBlogDetail } from '@/hooks/useBlogDetail';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error, refresh } = useBlogDetail(slug ?? '');
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center py-40">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <p className="mt-3 text-sm text-secondary-500">{t('blog.loadingArticle')}</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center py-40 text-center">
          <i className="ri-error-warning-line text-3xl text-red-500" />
          <h1 className="mt-3 text-xl font-bold text-secondary-900">
            {t('blog.couldNotLoadArticle')}
          </h1>
          <p className="mt-2 text-sm text-secondary-500">{error}</p>
          <button
            type="button"
            onClick={refresh}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm" />
            {t('blog.tryAgain')}
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center py-40">
          <h1 className="text-2xl font-bold text-secondary-900">
            {t('blog.articleNotFound')}
          </h1>
          <p className="mt-2 text-secondary-600">
            {t('blog.articleNotFoundText')}
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const { meta, toc, faq, contentBlocks } = data;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar dark />
      <title>{meta.title} | Vietnam Story</title>
      <meta name="description" content={meta.excerpt} />
      <main className="flex flex-col">
        <BlogHero meta={meta} />
        <BlogBreadcrumb title={meta.title} />
        <section className="mx-auto w-full max-w-[1200px] px-4 pb-10 sm:px-6 lg:px-6 lg:pb-[100px]">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-4">
            <div className="lg:hidden">
              <BlogMobileTOC toc={toc} />
            </div>

            <div className="w-full min-w-0 flex-1">
              <article className="flex w-full flex-col gap-5 rounded-xl bg-white p-4 shadow-[0_0_40px_0_rgba(0,0,0,0.06)] lg:p-5">
                <header className="flex flex-col gap-3.5">
                  <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-secondary-400">
                    <span>
                      {t('blog.by')}{' '}
                      <b className="font-semibold text-secondary-600">
                        {meta.author}
                      </b>
                    </span>
                    <span className="size-[3px] rounded-full bg-secondary-300" />
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-calendar-line text-xs" />
                      {meta.date}
                    </span>
                    <span className="size-[3px] rounded-full bg-secondary-300" />
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-time-line text-xs" />
                      {meta.readTime}
                    </span>
                  </div>
                </header>

                <ArticleRenderer blocks={contentBlocks} />
                <BlogFAQ faq={faq} />
                <BlogTags tags={meta.tags} title={meta.title} />
              </article>
            </div>

            <div className="hidden lg:block">
              <BlogSidebar toc={toc} />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}